// État : Balance générale (lecture seule, temps réel).
// Libellés d'interface neutres ; exports avec termes comptables standard.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { telechargerExport } from "@/lib/exportFichier";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import { Scale, Download, Printer, AlertTriangle, Loader2, ChevronLeft, ChevronRight, FileText } from "lucide-react";

const fmt = (n) => (n ? Math.round(n).toLocaleString("fr-FR") : "—");
const anneeCourante = new Date().getFullYear();

export default function BalanceGenerale() {
  const { company } = useAuth();
  const [exercices, setExercices] = useState([]);
  const [exercice, setExercice] = useState(String(anneeCourante));
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [classe, setClasse] = useState("toutes");
  const [tri, setTri] = useState("numero");
  const [soldeOuvert, setSoldeOuvert] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    api.get("/exercices").then(({ data }) => {
      setExercices(data || []);
      if (data && data.length) setExercice(String(data[0].annee));
    }).catch(() => {});
  }, []);

  const params = useCallback(() => ({
    exercice: parseInt(exercice, 10),
    date_debut: dateDebut || undefined,
    date_fin: dateFin || undefined,
    classe: classe === "toutes" ? undefined : parseInt(classe, 10),
    tri,
    solde_ouvert: soldeOuvert || undefined,
  }), [exercice, dateDebut, dateFin, classe, tri, soldeOuvert]);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/etats/balance", { params: { ...params(), page, page_size: 50 } });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setChargement(false);
    }
  }, [params, page]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { setPage(1); }, [exercice, dateDebut, dateFin, classe, tri, soldeOuvert]);

  const exporter = async (format) => {
    try {
      await telechargerExport("/etats/balance/export", params(), format, `balance_${exercice}`);
    } catch {
      toast.error("L'export a échoué. Veuillez réessayer.");
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total_comptes / data.page_size)) : 1;

  return (
    <div className="fenua-fade-up" data-testid="page-balance">
      {/* En-tête + actions */}
      <div className="no-print flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
            <Scale className="h-6 w-6 text-[#1D3D3D]" />
          </div>
          <div>
            <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">Balance générale</h1>
            <p className="text-sm text-[#1D3D3D]/70">Synthèse des comptes mouvementés de l'exercice.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" data-testid="export-csv" onClick={() => exporter("csv")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button variant="secondary" data-testid="export-xls" onClick={() => exporter("xls")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <Download className="h-4 w-4 mr-1" /> XLS
          </Button>
          <Button variant="secondary" data-testid="export-pdf" onClick={() => exporter("pdf")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <FileText className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button data-testid="bouton-imprimer" onClick={() => window.print()}
            className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
            <Printer className="h-4 w-4 mr-1" /> Imprimer
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="no-print mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg p-4 grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Exercice</Label>
          <Select value={exercice} onValueChange={setExercice}>
            <SelectTrigger data-testid="filtre-exercice" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(exercices.length ? exercices : [{ id: "x", annee: anneeCourante }]).map((e) => (
                <SelectItem key={e.id} value={String(e.annee)}>Exercice {e.annee}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Du</Label>
          <DatePicker testId="filtre-date-debut" value={dateDebut} onChange={setDateDebut} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Au</Label>
          <DatePicker testId="filtre-date-fin" value={dateFin} onChange={setDateFin} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Classe</Label>
          <Select value={classe} onValueChange={setClasse}>
            <SelectTrigger data-testid="filtre-classe" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="toutes">Toutes</SelectItem>
              {[1, 2, 3, 4, 5, 6, 7].map((c) => <SelectItem key={c} value={String(c)}>Classe {c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Tri</Label>
          <Select value={tri} onValueChange={setTri}>
            <SelectTrigger data-testid="filtre-tri" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="numero">Par numéro</SelectItem>
              <SelectItem value="solde">Par solde décroissant</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label data-testid="filtre-solde-ouvert" className="col-span-2 md:col-span-5 flex items-center gap-2 text-sm text-[#1D3D3D] cursor-pointer">
          <input type="checkbox" checked={soldeOuvert} onChange={(e) => setSoldeOuvert(e.target.checked)}
            className="h-4 w-4 accent-[#1D3D3D]" />
          Afficher uniquement les comptes avec solde ouvert (écritures non lettrées)
        </label>
      </div>

      {/* Tableau imprimable */}
      <div className="mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm overflow-hidden zone-impression">
        <div className="px-5 py-3 border-b border-[#4A8A8A]/20">
          <p className="font-bold text-[#1D3D3D]">{company?.raison_sociale}</p>
          <p className="text-xs text-[#1D3D3D]/60">Balance générale — Exercice {exercice}</p>
        </div>
        {chargement ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
        ) : !data || data.lignes.length === 0 ? (
          <p className="p-10 text-center text-sm text-[#1D3D3D]/60">Aucun mouvement pour ces critères.</p>
        ) : (
          <table className="w-full text-sm" data-testid="tableau-balance">
            <thead>
              <tr className="bg-[#E8F4F4] text-[#1D3D3D] text-left">
                <th className="px-4 py-2.5 font-semibold">Compte</th>
                <th className="px-4 py-2.5 font-semibold">Libellé</th>
                <th className="px-4 py-2.5 font-semibold text-right">Mouvements Débit</th>
                <th className="px-4 py-2.5 font-semibold text-right">Mouvements Crédit</th>
                <th className="px-4 py-2.5 font-semibold text-right">Solde Débit</th>
                <th className="px-4 py-2.5 font-semibold text-right">Solde Crédit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#4A8A8A]/10">
              {data.lignes.map((l) => (
                <tr key={l.numero} data-testid={`balance-ligne-${l.numero}`} className="hover:bg-[#E8F4F4]/40">
                  <td className="px-4 py-2 font-mono text-[#4A8A8A]">{l.numero}</td>
                  <td className="px-4 py-2 text-[#1D3D3D]">{l.libelle}</td>
                  <td className="px-4 py-2 text-right">{fmt(l.debit)}</td>
                  <td className="px-4 py-2 text-right">{fmt(l.credit)}</td>
                  <td className="px-4 py-2 text-right">{fmt(l.solde_debiteur)}</td>
                  <td className="px-4 py-2 text-right">{fmt(l.solde_crediteur)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-[#1D3D3D] text-white font-bold">
                <td className="px-4 py-3" colSpan={2}>TOTAUX</td>
                <td className="px-4 py-3 text-right" data-testid="total-debit">{fmt(data.total_debit)}</td>
                <td className="px-4 py-3 text-right" data-testid="total-credit">{fmt(data.total_credit)}</td>
                <td className="px-4 py-3" colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        )}

        {data && !data.equilibre && (
          <div data-testid="alerte-desequilibre" className="flex items-center gap-2 px-5 py-3 bg-red-50 border-t border-red-200 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4" /> Déséquilibre détecté : les totaux Débit et Crédit diffèrent.
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && totalPages > 1 && (
        <div className="no-print mt-4 flex items-center justify-center gap-3">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-precedente">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-[#1D3D3D]">Page {page} / {totalPages} · {data.total_comptes} comptes</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="page-suivante">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
